import React from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SEO_BY_PATH,
  getSeoForPath,
} from "../../lib/seoConfig";

/**
 * Route-aware document head.
 *
 * Mounted once inside <BrowserRouter> (see App.js) instead of per page, so
 * every route in seoConfig.js gets its title/description without touching
 * sixteen page components. Unlisted routes fall back to DEFAULT_SEO, which
 * mirrors public/index.html.
 */
export default function Seo() {
  const { pathname } = useLocation();

  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : "/";
  const { title, description, noindex } = getSeoForPath(pathname);

  // Only canonicalise routes we actually own. The "*" route renders Home for
  // any unmatched path, so a blanket self-canonical would mint canonicals for
  // URLs that shouldn't exist.
  const canonical =
    normalized === "/" || SEO_BY_PATH[normalized]
      ? `${SITE_URL}${normalized === "/" ? "/" : normalized}`
      : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {canonical && <meta property="og:url" content={canonical} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, follow" />}
    </Helmet>
  );
}
